import React, { useState, useEffect } from "react";
import ComputerIcon from "@mui/icons-material/Computer";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PersonIcon from "@mui/icons-material/Person";
import InventoryIcon from "@mui/icons-material/Inventory";
import ChatIcon from "@mui/icons-material/Chat";
import CircularProgress from "@mui/material/CircularProgress";
import api from "../config/api";

const BookDemoForm = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    product: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const productOptions = [
    { value: "hostel", label: "Hostel & Library Booking" },
  ];

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const getErrorMessage = (error) => {
    const data = error?.response?.data;

    if (!data) return "Something went wrong. Please try again.";

    // If backend sends {detail: "..."}
    if (typeof data?.detail === "string") return data.detail;

    // If backend sends validation errors like {email: ["..."], name: ["..."]}
    if (typeof data === "object") {
      const firstKey = Object.keys(data)[0];
      const val = data[firstKey];
      if (Array.isArray(val) && val.length) return val[0];
      if (typeof val === "string") return val;
    }

    return "Something went wrong. Please try again.";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    setLoading(true);
    setSuccessMsg("");
    setErrorMsg("");

    try {
      await api.post("book-demo/", formData);

      setSuccessMsg("Demo request submitted successfully!");
      setFormData({
        name: "",
        email: "",
        phone: "",
        company: "",
        product: "",
        message: "",
      });
    } catch (error) {
      console.error("Backend error response:", error.response?.data);
      setErrorMsg(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/10 via-accent to-primary/10 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary rounded-full mb-6">
            <ComputerIcon className="w-8 h-8 text-secondary" />
          </div>
          <h1 className="text-4xl font-bold text-secondary mb-4">
            Book a Product Demo
          </h1>
          <p className="text-xl text-secondary max-w-md mx-auto">
            Experience our innovative solutions firsthand. Schedule a
            personalized demonstration today!
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-secondary rounded-2xl shadow-xl overflow-hidden">
          <div className="bg-primary text-secondary overflow-hidden p-8">
            <h2 className="text-2xl font-bold">Demo Request Form</h2>
            <p className="text-secondary mt-2">
              Fill out the form below to schedule your demo
            </p>
          </div>

          <div className="p-8">
            {/* Success/Error Messages */}
            {successMsg && (
              <div className="mb-6 p-4 bg-primary/10 border border-primary/20 rounded-lg">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <CheckCircleIcon className="h-5 w-5 text-primary" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-primary">
                      {successMsg}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {errorMsg && (
              <div className="mb-6 p-4 bg-primary/10 border border-primary/20 rounded-lg">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <CancelIcon className="h-5 w-5 text-primary" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-primary">
                      {errorMsg}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6 text-accent">
              {/* Personal Information Section */}
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <PersonIcon className="w-5 h-5 text-primary mr-2" />
                  Contact Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      placeholder="Enter your full name"
                      className="w-full placeholder:text-secondary/80 text-secondary px-4 py-3 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition duration-200"
                      required
                      value={formData.name}
                      onChange={handleChange}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      placeholder="your.email@example.com"
                      className="w-full placeholder:text-secondary/80 text-secondary px-4 py-3 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition duration-200"
                      required
                      value={formData.email}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Phone Number
                    </label>
                    <input
                      type="text"
                      name="phone"
                      placeholder="+1 (555) 123-4567"
                      className="w-full placeholder:text-secondary/80 text-secondary px-4 py-3 border border-secondary/20 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition duration-200"
                      value={formData.phone}
                      onChange={handleChange}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Company Name
                    </label>
                    <input
                      type="text"
                      name="company"
                      placeholder="Your company name"
                      className="w-full placeholder:text-secondary/80 text-secondary px-4 py-3 border border-secondary/20 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition duration-200"
                      value={formData.company}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>

              {/* Product Selection Section */}
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <InventoryIcon className="w-5 h-5 text-primary mr-2" />
                  Product Selection
                </h3>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Select a Product *
                  </label>
                  <select
                    name="product"
                    className="w-full placeholder:text-secondary/80 text-secondary px-4 py-3 border border-secondary/20 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition duration-200"
                    value={formData.product}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Choose a product to demo</option>
                    {productOptions.map((product) => (
                      <option key={product.value} value={product.value}>
                        {product.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Additional Information Section */}
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <ChatIcon className="w-5 h-5 text-primary mr-2" />
                  Additional Information
                </h3>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Additional Message
                  </label>
                  <textarea
                    name="message"
                    placeholder="Tell us about your specific needs or requirements..."
                    className="w-full placeholder:text-secondary/80 text-secondary px-4 py-3 border border-secondary/20 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition duration-200"
                    rows="4"
                    value={formData.message}
                    onChange={handleChange}
                  ></textarea>
                </div>
              </div>

              {/* Submit Button */}
              <div className="pt-6">
                <button
                  type="submit"
                  className="w-full bg-primary text-secondary px-8 py-4 rounded-lg font-semibold text-lg hover:from-primary/90 hover:to-primary/70 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transform hover:scale-105 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  disabled={loading}
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <CircularProgress
                        size={20}
                        className="-ml-1 mr-3 text-secondary"
                      />
                      Submitting Request...
                    </div>
                  ) : (
                    "Submit Demo Request"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Footer Note */}
        <div className="text-center mt-8">
          <p className="text-secondary/80 text-sm">
            We'll review your demo request and get back to you within 24 hours
            to schedule your personalized demonstration.
          </p>
        </div>
      </div>
    </div>
  );
};

export default BookDemoForm;
